import * as Sentry from '@sentry/vue';

export const safeApplyFn = (fn) => {
	// copied from 'helpers.js' to avoid circular dependency
	try {
		return fn();
	} catch (err) {
		console.error(err);
		return null;
	}
};

export const ERROR_CODES = {
	TOKEN_INVALID: 'token_invalid',
	REFRESH_TOKEN: 'REFRESH_TOKEN', //client side only
	NOT_AUTHENTICATED: 'not_authenticated',
};

export const LOGOUT_REASON = {
	tokenExpired: 'token-expired',
};

export class IframeMessageError extends Error {
	constructor(message) {
		super(message);
		this.name = this.constructor.name;
	}
}
export class PlanPurchaseError extends Error {
	constructor(message) {
		super(message);
		this.name = this.constructor.name;
	}
}

export class NodeCreationError extends Error {
	constructor(message) {
		super(message);
		this.name = this.constructor.name;
	}
}

export class NodeUpdateError extends Error {
	constructor(message) {
		super(message);
		this.name = this.constructor.name;
	}
}

export class TaskProcessError extends Error {
	constructor(detail, code, metadata) {
		super(detail);
		this.detail = detail;
		this.code = code;
		this.metadata = metadata;
	}
}

export const STATUS_CODES = {
	BAD_REQUEST: 400,
	UNAUTHORIZED: 401,
	FORBIDDEN: 403,
	NOT_FOUND: 404,
};

function extractError(err) {
	return err?.response?.data?.error || err;
}

function extractErrorCode(err) {
	return err?.response?.data?.error?.code || err?.code;
}

function extractErrorMessage(err) {
	return err?.response?.data?.error?.msg || err?.message;
}

function extractErrorMetadata(err) {
	return err?.response?.data?.error?.metadata || err?.metadata;
}

export function extractStatusCode(err) {
	return err?.response?.status;
}

function throwError(errorCode) {
	throw new Error(errorCode);
}

// Sentry related handlers

/* Public */

function setContext(contextName, contextObject) {
	// we use this privately so we don't destruct related objects outside and use 'safeApplyFn' to be on the safe side
	// create a specific API for each contextName (e.g. 'setUserMetadata')
	safeApplyFn(() => {
		if (Object.keys(contextObject).includes('type')) {
			console.error("Do not use 'type' property as it is a reserved word in Sentry ' ");
		}
		Sentry.setContext(contextName, contextObject);
	});
}
/* Public */
function setUser(user) {
	safeApplyFn(() => {
		Sentry.setUser({ id: user.id });
	});
}

function clear() {
	safeApplyFn(() => {
		Sentry.configureScope((scope) => scope.clear());
		Sentry.setUser(null);
	});
}

function setUserMetadata(user) {
	safeApplyFn(() => {
		const metadata = user.profile.metadata;
		const propsToSet = ['signup_method', 'studio_mode'];
		const data = Object.keys(metadata).reduce(
			(result, key) => (propsToSet.includes(key) ? { ...result, [key]: metadata[key] } : result),
			{}
		);
		setContext('user_metadata', {
			/* Do not use 'type' property as it is a reserved word! */
			metadata: data,
		});
	});
}

function safeLog(fnReturningString) {
	safeApplyFn(() => {
		if (typeof fnReturningString !== 'function') {
			console.log(fnReturningString);
		} else {
			const str = fnReturningString();
			console.log(str);
		}
	});
}

const ErrorHandlingService = {
	/* RestAPI related handlers */
	extractError,
	extractErrorCode,
	extractErrorMessage,
	extractErrorMetadata,
	extractStatusCode,

	/*General handlers*/
	throwError,

	// Sentry related handlers
	setUser,
	setUserMetadata,
	clear,
	safeLog,
};

export default ErrorHandlingService;
